import { Form, Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import Select from "../../components/Select";
import PermissionWrapper from "../../components/PermissionWrapper";
import { findCollectionByKeys, normalizeOptions } from "../../utils/helper";
import {
  fetchIssueEquipmentDropdown,
  fetchIssueTyresDropdown,
  fetchIssueVehiclesDropdown,
} from "../../store/Slices/IssuesSlice";
import {
  clearEquipmentDetailState,
  fetchEquipmentServiceDetail,
} from "../../store/Slices/EquipmentSlice";
import {
  clearVehicleDetail,
  createServiceEntry,
  fetchNextServiceId,
  fetchServiceTagsDropdown,
  fetchVehicleDetail,
} from "../../store/Slices/ServiceSlice";
import {
  clearTyreDetailState,
  fetchTyreServiceDetail,
} from "../../store/Slices/TyreSlice";
import "../../styles/ServiceEntry.css";
import { maintenanceTypesDropdown, repairPriorityDropdown } from "../../utils/Dropdowns";
import { formatUtcDateTimeForApi, slugfy } from "../../utils/helper";
import { createServiceEntryValidationSchema } from "../../utils/Schema";
import { errorNotify, successNotify } from "../../utils/toast";
import IssueTracking from "./IssueTracking";
import ServiceLineItem from "./ServiceLineItem";
import {
  AppMainModules,
  MaintinaceSubModules,
  ServicesPermission,
} from "../../utils/app-constant";

const AssetSourceTypeEnum = {
  Vehicle: "vehicle",
  Equipment: "equipment",
  Tyre: "tyre",
};

const assetTypeOptions = [
  { label: "Vehicle", value: AssetSourceTypeEnum.Vehicle },
  { label: "Equipment", value: AssetSourceTypeEnum.Equipment },
  { label: "Tyre", value: AssetSourceTypeEnum.Tyre },
];
const serviceWorkflowAssetTypes = [
  AssetSourceTypeEnum.Vehicle,
  AssetSourceTypeEnum.Equipment,
  AssetSourceTypeEnum.Tyre,
];

const getPayloadId = (value) => {
  if (value === "" || value === null || value === undefined) {
    return value;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : value;
};

const extractAssetMeterValue = (detailData) => {
  const candidates = [
    detailData?.odm,
    detailData?.odo_meter,
    detailData?.usage_meter_reading,
    detailData?.usageMeterReading,
    detailData?.meter_reading,
    detailData?.distance,
    detailData?.distance_covered,
    detailData?.distanceCovered,
    detailData?.total_km_run,
    detailData?.totalKmRun,
    detailData?.covered_distance,
    detailData?.vehicle?.odm,
    detailData?.vehicle?.odo_meter,
    detailData?.last_service?.odo_meter,
  ];

  for (const candidate of candidates) {
    if (candidate === "" || candidate === null || candidate === undefined) {
      continue;
    }

    return candidate;
  }

  return "";
};

const buildRefNumber = (serviceId) => {
  const normalizedServiceId = getPayloadId(serviceId);
  if (
    normalizedServiceId === "" ||
    normalizedServiceId === null ||
    normalizedServiceId === undefined
  ) {
    return "";
  }

  return `SE-${normalizedServiceId}`;
};

const getAssetServiceLineItems = (detailData) => {
  if (Array.isArray(detailData?.asset_service_line_items)) {
    return detailData.asset_service_line_items;
  }

  if (Array.isArray(detailData?.service_line_items)) {
    return detailData.service_line_items;
  }

  return [];
};

const calculateCostSummary = (detailData) => {
  const items = getAssetServiceLineItems(detailData);
  const cost_parts = items.reduce(
    (sum, item) => sum + parseFloat(item?.cost_parts ?? item?.rate ?? 0),
    0
  );
  const cost_labor = items.reduce(
    (sum, item) => sum + parseFloat(item?.cost_labor ?? 0),
    0
  );
  const sub_total = cost_parts + cost_labor;

  return {
    cost_parts: cost_parts.toFixed(2),
    cost_labor: cost_labor.toFixed(2),
    sub_total: sub_total.toFixed(2),
  };
};

const ServiceEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedAssetType, setSelectedAssetType] = useState("");
  const [selectedAssetOption, setSelectedAssetOption] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [link_issue_ids, setLink_issue_ids] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(0);
  const [checkedIssueIds, setCheckedIssueIds] = useState(() => {
    const saved = localStorage.getItem(`checkedIssueIds_new`);
    return saved ? JSON.parse(saved) : [];
  });

  const {
    vehicleDetail,
    vehicleDetailLoading,
    createServiceLoading,
    nextServiceId,
    serviceTagsData,
    serviceTagsLoading,
  } = useSelector((state) => state.services);
  const {
    equipmentDetailData,
    equipmentDetailLoading,
  } = useSelector((state) => state.equipment);
  const {
    tyreDetailData,
    tyreDetailLoading,
  } = useSelector((state) => state.tyre);
  const {
    issueVehiclesDropdownData,
    issueVehiclesDropdownLoading,
    issueEquipmentDropdownData,
    issueEquipmentDropdownLoading,
    issueTyresDropdownData,
    issueTyresDropdownLoading,
  } = useSelector((state) => state.issues);

  useEffect(() => {
    const storageKey = `checkedIssueIds_new`;
    localStorage.setItem(storageKey, JSON.stringify(checkedIssueIds));
  }, [checkedIssueIds]);

  useEffect(() => {
    dispatch(fetchIssueVehiclesDropdown());
    dispatch(fetchIssueEquipmentDropdown());
    dispatch(fetchIssueTyresDropdown());
    dispatch(fetchServiceTagsDropdown());
    dispatch(fetchNextServiceId());

    return () => {
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());
      localStorage.removeItem(`checkedIssueIds_new`);
    };
  }, [dispatch]);

  const vehicleOptions = useMemo(() => {
    const source = Array.isArray(issueVehiclesDropdownData)
      ? issueVehiclesDropdownData
      : findCollectionByKeys(issueVehiclesDropdownData, [
          "vehicles",
          "vehicle",
          "vehiclesdropdown",
          "vehicledropdown",
          "vehiclelist",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "vehicle_id", "ref_id"],
      labelKeys: [
        "label",
        "registration_number",
        "registration_no",
        "vehicle_registration_number",
        "name",
        "title",
      ],
      defaultLabelPrefix: "Vehicle",
    });
  }, [issueVehiclesDropdownData]);

  const equipmentOptions = useMemo(() => {
    const source = Array.isArray(issueEquipmentDropdownData)
      ? issueEquipmentDropdownData
      : findCollectionByKeys(issueEquipmentDropdownData, [
          "equipments",
          "equipmentdropdown",
          "equipment",
          "equipmentlist",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "equipment_id"],
      labelKeys: [
        "label",
        "equipment_id",
        "title",
        "name",
        "serial_number",
        "registration_number",
      ],
      defaultLabelPrefix: "Equipment",
    });
  }, [issueEquipmentDropdownData]);

  const tyreOptions = useMemo(() => {
    const source = Array.isArray(issueTyresDropdownData)
      ? issueTyresDropdownData
      : findCollectionByKeys(issueTyresDropdownData, [
          "tyres",
          "tyre",
          "tyresdropdown",
          "tyredropdown",
          "tyrelist",
        ]);

    return normalizeOptions(source, {
      valueKeys: ["value", "id", "tyre_id", "system_id"],
      labelKeys: [
        "label",
        "tyre_id",
        "serial_number",
        "system_id",
        "name",
        "title",
      ],
      defaultLabelPrefix: "Tyre",
    });
  }, [issueTyresDropdownData]);

  const tagsOptions = (serviceTagsData || []).map((tag) => ({
    label: tag.tag_name,
    value: tag.tag_name,
  }));

  const resolveDraftServiceId = async () => {
    const normalizedServiceId = getPayloadId(nextServiceId);
    if (
      normalizedServiceId !== "" &&
      normalizedServiceId !== null &&
      normalizedServiceId !== undefined
    ) {
      return normalizedServiceId;
    }

    const response = await dispatch(fetchNextServiceId()).unwrap();
    return getPayloadId(response?.data ?? response);
  };

  const getCurrentAssetDetail = (assetType) => {
    if (assetType === AssetSourceTypeEnum.Vehicle) {
      return vehicleDetail;
    }

    if (assetType === AssetSourceTypeEnum.Equipment) {
      return equipmentDetailData;
    }

    if (assetType === AssetSourceTypeEnum.Tyre) {
      return tyreDetailData;
    }

    return null;
  };

  const getCurrentAssetDetailLoading = (assetType) => {
    if (assetType === AssetSourceTypeEnum.Vehicle) {
      return vehicleDetailLoading;
    }

    if (assetType === AssetSourceTypeEnum.Equipment) {
      return equipmentDetailLoading;
    }

    if (assetType === AssetSourceTypeEnum.Tyre) {
      return tyreDetailLoading;
    }

    return false;
  };

  const initialValues = {
    asset_type: "",
    asset_id: "",
    odo_meter: "",
    odm: "",
    ref_number: "",
    status: "open",
    priority: "",
    services_type: "",
    start_at: "",
    complete_at: "",
    note: "",
    tags: [],
  };

  const handleAssetTypeChange = (selectedOption, setFieldValue) => {
    const nextAssetType = selectedOption?.value || "";

    setSelectedAssetType(nextAssetType);
    setSelectedAssetOption(null);
    setSelectedVehicle(null);
    setLink_issue_ids([]);
    setCheckedIssueIds([]);
    dispatch(clearVehicleDetail());
    dispatch(clearEquipmentDetailState());
    dispatch(clearTyreDetailState());

    setFieldValue("asset_type", nextAssetType);
    setFieldValue("asset_id", "");
    setFieldValue("odo_meter", "");
    setFieldValue("odm", "");
    setFieldValue("ref_number", "");
  };

  const handleAssetChange = async (selectedOption, assetType, setFieldValue) => {
    setSelectedAssetType(assetType || "");
    setSelectedAssetOption(selectedOption || null);
    setSelectedVehicle(
      assetType === AssetSourceTypeEnum.Vehicle ? selectedOption || null : null
    );
    setLink_issue_ids([]);
    setCheckedIssueIds([]);

    setFieldValue("asset_id", selectedOption?.value || "");
    setFieldValue("odo_meter", "");
    setFieldValue("odm", "");

    if (!selectedOption) {
      setFieldValue("ref_number", "");
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());
      return;
    }

    let draftServiceId = null;
    try {
      draftServiceId = await resolveDraftServiceId();
      setFieldValue("ref_number", buildRefNumber(draftServiceId));
    } catch (error) {
      setFieldValue("ref_number", "");
      errorNotify(error);
      return;
    }

    dispatch(clearVehicleDetail());
    dispatch(clearEquipmentDetailState());
    dispatch(clearTyreDetailState());

    if (assetType === AssetSourceTypeEnum.Vehicle) {
      try {
        const response = await dispatch(
          fetchVehicleDetail({
            vehicleId: selectedOption.value,
            vehicleBrandId: selectedOption.make_id,
            ref_id: selectedOption.ref_id,
          })
        ).unwrap();

        const detailData = response?.data || {};
        const meterValue = extractAssetMeterValue(detailData);
        setFieldValue(
          "odo_meter",
          detailData?.last_service?.odo_meter ?? meterValue ?? ""
        );
        setFieldValue("odm", meterValue ?? "");
        setFieldValue(
          "ref_number",
          buildRefNumber(draftServiceId)
        );
      } catch (error) {
        errorNotify(error);
      }

      return;
    }

    if (assetType === AssetSourceTypeEnum.Equipment) {
      try {
        const equipmentServiceId = await resolveDraftServiceId();
        const response = await dispatch(
          fetchEquipmentServiceDetail({
            equipmentId: selectedOption.value,
            serviceId: equipmentServiceId,
          })
        ).unwrap();
        const detailData = response?.data || response || {};
        const meterValue = extractAssetMeterValue(detailData);
        setFieldValue("odo_meter", meterValue ?? "");
        setFieldValue("odm", meterValue ?? "");
      } catch (error) {
        setFieldValue("odo_meter", "");
        setFieldValue("odm", "");
        errorNotify(error);
      }

      return;
    }

    if (assetType === AssetSourceTypeEnum.Tyre) {
      try {
        const tyreServiceId = await resolveDraftServiceId();
        const response = await dispatch(
          fetchTyreServiceDetail({
            tyreId: selectedOption.value,
            serviceId: tyreServiceId,
          })
        ).unwrap();
        const detailData = response?.data || response || {};
        const meterValue = extractAssetMeterValue(detailData);
        setFieldValue("odo_meter", meterValue ?? "");
        setFieldValue("odm", meterValue ?? "");
      } catch (error) {
        setFieldValue("odo_meter", "");
        setFieldValue("odm", "");
        errorNotify(error);
      }
    }
  };

  const handleDiscountChange = (e) => {
    let val = e.target.value.replace(/[^0-9.]/g, "");
    val = val === "" ? "" : Math.max(0, Math.min(100, Number(val)));
    setDiscountPercent(val);
  };

  const handleTaxChange = (e) => {
    let val = e.target.value.replace(/[^0-9.]/g, "");
    val = val === "" ? "" : Math.max(0, Math.min(100, Number(val)));
    setTaxPercent(val);
  };

  const calculateTotal = (costSummary) => {
    const subTotal = parseFloat(costSummary.sub_total);
    const discount = parseFloat(discountPercent) || 0;
    const tax = parseFloat(taxPercent) || 0;
    const discountAmount = subTotal * (discount / 100);
    const taxAmount = subTotal * (tax / 100);
    return (subTotal + taxAmount - discountAmount).toFixed(2);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const isVehicleAsset = values.asset_type === AssetSourceTypeEnum.Vehicle;
      const supportsServiceWorkflow = serviceWorkflowAssetTypes.includes(
        values.asset_type
      );
      const currentAssetDetail = getCurrentAssetDetail(values.asset_type);
      const currentLineItems = getAssetServiceLineItems(currentAssetDetail);
      const costSummary = calculateCostSummary(currentAssetDetail);

      if (supportsServiceWorkflow && currentLineItems.length === 0) {
        errorNotify("Atleast one Service Line Item is required!");
        return;
      }

      if (discountPercent < 0 || discountPercent > 100) {
        errorNotify("Discount must be between 0 and 100%");
        return;
      }
      if (taxPercent < 0 || taxPercent > 100) {
        errorNotify("Tax must be between 0 and 100%");
        return;
      }

      const subTotal = parseFloat(costSummary.sub_total);
      const discount = parseFloat(discountPercent) || 0;
      const tax = parseFloat(taxPercent) || 0;
      const discountAmount = subTotal * (discount / 100);
      const taxAmount = subTotal * (tax / 100);
      const complete_total = subTotal + taxAmount - discountAmount;
      const meterValue = parseInt(values.odm);

      const serviceData = {
        asset_id: getPayloadId(values.asset_id),
        asset_type: values.asset_type,
        ...(meterValue !== null && !Number.isNaN(meterValue)
          ? { odo_meter: meterValue }
          : {}),
        ...(isVehicleAsset && meterValue !== null && !Number.isNaN(meterValue)
          ? { odm: meterValue }
          : {}),
        status: values.status,
        priority: values.priority,
        ref_number: values.ref_number,
        services_type: values.services_type,
        start_at: formatUtcDateTimeForApi(values.start_at),
        complete_at: formatUtcDateTimeForApi(values.complete_at),
        note: values.note,
        cost_parts: parseFloat(costSummary.cost_parts),
        cost_labor: parseFloat(costSummary.cost_labor),
        cost_tax: taxPercent,
        discount: discountPercent,
        sub_total: subTotal,
        complete_total: complete_total,
        tags: values.tags.map((tag) => tag.value || tag),
        files: uploadedFiles.map((file) => file.fileName),
        link_issue_ids: supportsServiceWorkflow ? link_issue_ids : [],
        line_item: supportsServiceWorkflow
          ? currentLineItems.map((item) => ({
              line_item_id: item.id,
              service_issue_id: item.service_issue_id,
            })) || []
          : [],
      };

      await dispatch(createServiceEntry(serviceData)).unwrap();
      successNotify("Service entry created successfully");

      localStorage.removeItem(`checkedIssueIds_new`);

      resetForm();
      setSelectedAssetType("");
      setSelectedAssetOption(null);
      setSelectedVehicle(null);
      setUploadedFiles([]);
      setLink_issue_ids([]);
      setCheckedIssueIds([]);
      setDiscountPercent(0);
      setTaxPercent(0);
      dispatch(clearVehicleDetail());
      dispatch(clearEquipmentDetailState());
      dispatch(clearTyreDetailState());
      navigate("/services");
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  const refreshVehicleDetail = async (source = "both", refIdOverride = null) => {
    if (selectedAssetType === AssetSourceTypeEnum.Vehicle && selectedVehicle) {
      const response = await dispatch(
        fetchVehicleDetail({
          vehicleId: selectedVehicle.value,
          vehicleBrandId: selectedVehicle.make_id,
          ref_id: refIdOverride ?? selectedVehicle.ref_id,
        })
      ).unwrap();
      return { response, source };
    }

    if (
      selectedAssetType === AssetSourceTypeEnum.Equipment &&
      selectedAssetOption?.value
    ) {
      const serviceId = refIdOverride ?? (await resolveDraftServiceId());
      const response = await dispatch(
        fetchEquipmentServiceDetail({
          equipmentId: selectedAssetOption.value,
          serviceId,
        })
      ).unwrap();
      return { response, source };
    }

    if (
      selectedAssetType === AssetSourceTypeEnum.Tyre &&
      selectedAssetOption?.value
    ) {
      const serviceId = refIdOverride ?? (await resolveDraftServiceId());
      const response = await dispatch(
        fetchTyreServiceDetail({
          tyreId: selectedAssetOption.value,
          serviceId,
        })
      ).unwrap();
      return { response, source };
    }
  };

  return (
    <>
      <Breadcrumbs values={["Maintenance", "Overview", "New Service Entry"]} />

      <Formik
        initialValues={initialValues}
        validationSchema={createServiceEntryValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => {
          const selectedAssetType = values.asset_type || "";
          const isVehicleAsset =
            selectedAssetType === AssetSourceTypeEnum.Vehicle;
          const supportsServiceWorkflow = serviceWorkflowAssetTypes.includes(
            selectedAssetType
          );
          const currentAssetOptions =
            selectedAssetType === AssetSourceTypeEnum.Vehicle
              ? vehicleOptions
              : selectedAssetType === AssetSourceTypeEnum.Equipment
                ? equipmentOptions
                : selectedAssetType === AssetSourceTypeEnum.Tyre
                  ? tyreOptions
                  : [];
          const currentAssetLoading =
            selectedAssetType === AssetSourceTypeEnum.Vehicle
              ? issueVehiclesDropdownLoading
              : selectedAssetType === AssetSourceTypeEnum.Equipment
                ? issueEquipmentDropdownLoading
                : selectedAssetType === AssetSourceTypeEnum.Tyre
                  ? issueTyresDropdownLoading
                  : false;
          const currentAssetLabel =
            selectedAssetType === AssetSourceTypeEnum.Vehicle
              ? "Vehicle"
              : selectedAssetType === AssetSourceTypeEnum.Equipment
                ? "Equipment"
                : selectedAssetType === AssetSourceTypeEnum.Tyre
                  ? "Tyre"
                  : "Asset";
          const currentAssetPlaceholder = selectedAssetType
            ? `Select ${currentAssetLabel}`
            : "Select Asset Type First";
          const selectedAssetValue =
            currentAssetOptions.find(
              (option) => String(option.value) === String(values.asset_id)
            ) ||
            selectedAssetOption ||
            null;
          const currentAssetDetail = getCurrentAssetDetail(selectedAssetType);
          const currentAssetDetailLoading =
            getCurrentAssetDetailLoading(selectedAssetType);
          const costSummary = calculateCostSummary(currentAssetDetail);
          const totalAmount = calculateTotal(costSummary);

          return (
            <Form>
              <div className="access_view">
                <h2>New Service Entry</h2>

                <div className="service_entry_btn">
                  <PermissionWrapper
                    module={MaintinaceSubModules.Services}
                    permission={ServicesPermission.Create}
                    mainModule={AppMainModules.maintenance}
                  >
                    <Button
                      cancelBtn={() => navigate("/services")}
                      loading={createServiceLoading}
                      disabled={createServiceLoading || isSubmitting}
                      submitText="Save Service Entry"
                      type="submit"
                      shouldRow={false}
                    />
                  </PermissionWrapper>
                </div>
              </div>

              <div className="service_entry_main">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-md-8">
                      <div className="service_entry_details">
                        <h6>Service Entry Details</h6>
                        <p>Comprehensive information for this service record.</p>

                        <div className="vehicle_details_fields">
                          <h5>Asset Details</h5>
                          <Row>
                            <Col md={6}>
                              <Select
                                label="Assets Type"
                                placeholder="Select Asset Type"
                                options={assetTypeOptions}
                                loading={false}
                                name="asset_type"
                                value={assetTypeOptions.find(
                                  (option) => option.value === values.asset_type
                                )}
                                onChange={(selectedOption) =>
                                  handleAssetTypeChange(
                                    selectedOption,
                                    setFieldValue
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.asset_type}
                                touched={touched.asset_type}
                                isSearch={false}
                              />
                            </Col>
                            <Col md={6}>
                              <Select
                                label={currentAssetLabel}
                                placeholder={currentAssetPlaceholder}
                                options={currentAssetOptions}
                                loading={currentAssetLoading}
                                name="asset_id"
                                value={selectedAssetValue}
                                onChange={(selectedOption) =>
                                  handleAssetChange(
                                    selectedOption,
                                    selectedAssetType,
                                    setFieldValue
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.asset_id}
                                touched={touched.asset_id}
                                isSearch={true}
                                isDisabled={!selectedAssetType}
                              />
                            </Col>
                            <Col md={6}>
                              <Select
                                label="Repair Priority"
                                placeholder="Select Repair Priority"
                                options={repairPriorityDropdown}
                                loading={false}
                                name="priority"
                                value={repairPriorityDropdown.find(
                                  (option) => option.value === values.priority
                                )}
                                onChange={(selectedOption) =>
                                  setFieldValue(
                                    "priority",
                                    selectedOption?.value || ""
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.priority}
                                touched={touched.priority}
                                isSearch={false}
                              />
                            </Col>
                            {selectedAssetType && (
                              <Col md={6}>
                                <Input
                                  label="Odometer Reading / Distance"
                                  placeholder="Enter Odometer Reading / Distance"
                                  type="number"
                                  name="odm"
                                  value={values.odm}
                                  showExtraText={
                                    isVehicleAsset &&
                                    values.odo_meter !== "" &&
                                    values.odo_meter !== null &&
                                    values.odo_meter !== undefined &&
                                    String(values.odo_meter) !== String(values.odm)
                                  }
                                  extraText={`Last Recorded: ${
                                    values?.odo_meter || 0
                                  } KM`}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.odm}
                                  touched={touched.odm}
                                />
                              </Col>
                            )}
                            <Col md={6}>
                              <Select
                                label="Maintenance Type"
                                placeholder="Select Maintenance Type"
                                options={maintenanceTypesDropdown}
                                loading={false}
                                name="services_type"
                                value={maintenanceTypesDropdown.find(
                                  (option) =>
                                    option.value === values.services_type
                                )}
                                onChange={(selectedOption) =>
                                  setFieldValue(
                                    "services_type",
                                    selectedOption?.value || ""
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.services_type}
                                touched={touched.services_type}
                                isSearch={false}
                              />
                            </Col>
                            <Col md={6}>
                              <Input
                                label="Reference Number"
                                placeholder="Enter Reference"
                                type="text"
                                name="ref_number"
                                value={values.ref_number}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.ref_number}
                                touched={touched.ref_number}
                                disabled
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="vehicle_details_fields">
                          <h5>Repair Details</h5>
                          <Row>
                            <Col md={6}>
                              <Input
                                label="Completion Date & Time"
                                placeholder=""
                                type="datetime-local"
                                name="complete_at"
                                value={values.complete_at}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.complete_at}
                                touched={touched.complete_at}
                                minValue={
                                  values.start_at ? `${values.start_at}T00:00` : undefined
                                }
                              />
                            </Col>
                            <Col md={6}>
                              <Input
                                label="Start Date (Optional)"
                                placeholder=""
                                type="date"
                                name="start_at"
                                value={values.start_at}
                                onChange={(event) => {
                                  handleChange(event);
                                  const nextStartDate = event.target.value;
                                  if (
                                    nextStartDate &&
                                    values.complete_at &&
                                    new Date(values.complete_at).getTime() <
                                      new Date(nextStartDate).getTime()
                                  ) {
                                    setFieldValue("complete_at", "");
                                  }
                                }}
                                onBlur={handleBlur}
                                error={errors.start_at}
                                touched={touched.start_at}
                                required={false}
                              />
                            </Col>
                            <Col md={6}>
                              <Select
                                label="Tags"
                                placeholder="Type and press Enter to create tags"
                                options={tagsOptions}
                                loading={serviceTagsLoading}
                                name="tags"
                                value={values.tags}
                                onChange={(selectedOptions) =>
                                  setFieldValue("tags", selectedOptions || [])
                                }
                                onBlur={handleBlur}
                                isMulti={true}
                                isCreatable={true}
                                error={
                                  errors.tags && typeof errors.tags === "string"
                                    ? errors.tags
                                    : null
                                }
                                touched={touched.tags}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {supportsServiceWorkflow ? (
                        <>
                          <IssueTracking
                            setLink_issue_ids={setLink_issue_ids}
                            vehicleDetailLoading={currentAssetDetailLoading}
                            vehicleDetail={currentAssetDetail}
                            selectedVehicle={selectedAssetValue}
                            assetType={selectedAssetType}
                            assetId={getPayloadId(values.asset_id)}
                            assetLabel={currentAssetLabel}
                            resolveDraftServiceId={resolveDraftServiceId}
                            refreshVehicleDetail={() =>
                              refreshVehicleDetail("issue")
                            }
                            checkedIssueIds={checkedIssueIds}
                            setCheckedIssueIds={setCheckedIssueIds}
                          />
                          <ServiceLineItem
                            vehicleDetailLoading={currentAssetDetailLoading}
                            vehicleDetail={currentAssetDetail}
                            selectedVehicle={selectedAssetValue}
                            assetType={values.asset_type}
                            assetId={getPayloadId(values.asset_id)}
                            assetLabel={currentAssetLabel}
                            refreshVehicleDetail={(serviceId) =>
                              refreshVehicleDetail("service", serviceId)
                            }
                          />
                        </>
                      ) : null}

                      <div className="photo_document_section">
                        <h6>Photos & Documents</h6>
                        <p>
                          Upload relevant photos or documents for this service.
                        </p>

                        <FileUpload
                          destination="service-entry"
                          files={uploadedFiles}
                          setFiles={setUploadedFiles}
                          maxFileSize={10}
                        />
                      </div>

                      <div className="my-4">
                        <PermissionWrapper
                          module={MaintinaceSubModules.Services}
                          permission={ServicesPermission.Create}
                          mainModule={AppMainModules.maintenance}
                        >
                          <Button
                            cancelBtn={() => navigate("/services")}
                            loading={createServiceLoading}
                            disabled={createServiceLoading || isSubmitting}
                            submitText="Save Service Entry"
                            type="submit"
                          />
                        </PermissionWrapper>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="service_cost_summary_details">
                        <div className="service_cost_summary">
                          <h6>Cost Summary</h6>
                          <p>Financial breakdown of the service entries.</p>

                          <div className="subtotal">
                            <div>Subtotal (Calculated)</div>
                            <span>PKR {costSummary.sub_total}</span>
                          </div>

                          <div className="service_item">
                            <div>Parts</div>
                            <div>PKR {costSummary.cost_parts}</div>
                          </div>
                          <div className="service_item">
                            <div>Labor</div>
                            <div>PKR {costSummary.cost_labor}</div>
                          </div>
                          <div className="service_item">
                            <div>Discount (%)</div>
                            <div>
                              <input
                                className="edit_input"
                                type="number"
                                min={0}
                                max={100}
                                value={discountPercent}
                                onChange={handleDiscountChange}
                                placeholder="0"
                              />{" "}
                              %
                            </div>
                          </div>
                          <div className="service_item">
                            <div>Tax (%)</div>
                            <div>
                              <input
                                className="edit_input"
                                type="number"
                                min={0}
                                max={100}
                                value={taxPercent}
                                onChange={handleTaxChange}
                                placeholder="0"
                              />{" "}
                              %
                            </div>
                          </div>

                          <div className="total">
                            <div>Total</div>
                            <span>PKR {totalAmount}</span>
                          </div>
                        </div>

                        <div className="general_notes">
                          <h6>General Notes</h6>
                          <p>Add any additional notes or observations here.</p>

                          <textarea
                            placeholder="Type your general notes here..."
                            rows={7}
                            name="note"
                            value={values.note}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {touched.note && errors.note && (
                            <div
                              className="error-message"
                              style={{
                                color: "#ff0000de",
                                fontSize: "12px",
                                marginTop: "3px",
                              }}
                            >
                              {errors.note}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default ServiceEntry;
